/**
 * The measurement field — one key type and one wire parser.
 *
 * `MeasurementFieldKey` is this app's name for a backend `MeasurementField` enum value. The wire
 * ships the PascalCase enum name (`"Weight"`, `"Rpe"`, `"Duration"`); this module lower-cases it
 * and does nothing else. There are deliberately NO aliases: an alias table is a place for two
 * surfaces to disagree, and every alias the mobile app's old tables carried (`"kg"`, `"time"`,
 * `"durationseconds"`) named a value the backend has never emitted.
 *
 * The rule that cost a client their session on 30 July 2026 lives here as its ABSENCE: **an empty
 * list stays an empty list.** An exercise that measures nothing renders no columns — that is a
 * first-class configuration, not missing data to be guessed at, and substituting weight × reps for
 * it is what put an off-contract value on the wire and made `/complete` unclosable.
 *
 * ── This file MOVED here in PR-B2C, on its second consumer ───────────────────────────────────
 * It shipped in `entities/plan-draft/model/` in PR-A1, where `ExerciseDraft` was its only reader,
 * and its own header scheduled the promotion for the commit that gave it a second one. That is
 * this commit: the set table (`features/plan-day-programming`) and the library rail
 * (`features/exercise-library-rail`) both read the contract, and a FEATURE may not import an
 * entity's internals — only its barrel — so a type every feature needs cannot live inside one.
 *
 * `fieldDictionary.ts` arrived with it, which is the other half of the same promotion: the parse
 * answers "which fields", the dictionary answers "in what order, in what unit, between what
 * bounds". `parseMeasurementFields` still returns the wire's own order and deliberately does not
 * sort — `measurementFieldsFromWire` is the entry point that does, and it is the only one a
 * rendering surface may call (R4: one order, everywhere).
 */

/**
 * Every measurement field the backend can name. Closed on purpose: the backend enum is the
 * authority on which values exist, and a bare `string` key would let a typo become a production
 * defect with no red build.
 */
export type MeasurementFieldKey =
  | 'weight'
  | 'reps'
  | 'duration'
  | 'distance'
  | 'rounds'
  | 'speed'
  | 'rpe'
  | 'rir'
  | 'tempo';

/**
 * How a field's value BEHAVES, as the backend's dictionary declares it. Five kinds, and the
 * distinctions are load-bearing rather than descriptive:
 *
 * · `Integer` and `BoundedNumberWithStep` are NOT interchangeable. `Rir` is `int?` on the backend
 *   while carrying the same `BoundedNumberWithStep` type as the decimal `Rpe`, so wholeness is read
 *   off the STEP and never off this — see `isWholeNumberField` in `measurementValues.ts`.
 * · `TextNotation` is the one kind with no step and no bounds, which is what makes `step`, `min`
 *   and `max` nullable on the definition rather than merely optional.
 */
export type MeasurementValueType =
  | 'NumberWithUnit'
  | 'Integer'
  | 'ElapsedTime'
  | 'BoundedNumberWithStep'
  | 'TextNotation';

/**
 * Every key, as an EXHAUSTIVE record rather than a list. The compiler holds the two in step:
 * widening the union without adding a row here is an error, and a row for a key the union does not
 * have is an error too. A list would compile happily while half-populated, and the missing field
 * would then vanish from every rule that reads this.
 */
const FIELD_KEY_TABLE: Record<MeasurementFieldKey, true> = {
  weight: true,
  reps: true,
  duration: true,
  distance: true,
  rounds: true,
  speed: true,
  rpe: true,
  rir: true,
  tempo: true,
};

/** Every field key this build knows — the app's claim about what fields exist. */
export const ALL_MEASUREMENT_FIELD_KEYS: readonly MeasurementFieldKey[] = Object.keys(
  FIELD_KEY_TABLE,
) as MeasurementFieldKey[];

const FIELD_KEYS: ReadonlySet<string> = new Set<string>(ALL_MEASUREMENT_FIELD_KEYS);

/**
 * One wire value → one key, or `null` when the backend named something this build does not know —
 * a field added on the server before this app shipped its definition. Case-insensitive because the
 * wire is PascalCase and the key is lower-case; that is the whole translation.
 */
export function parseMeasurementField(value: string | null | undefined): MeasurementFieldKey | null {
  if (!value) return null;
  const key = value.trim().toLowerCase();
  return FIELD_KEYS.has(key) ? (key as MeasurementFieldKey) : null;
}

/**
 * One key → the enum name the backend expects. The inverse of `parseMeasurementField` and its
 * neighbour on purpose: a second table mapping the same nine values would be a place for the two
 * directions to disagree, and the key IS the lower-cased enum name, so the translation is the
 * capital letter and nothing else.
 */
export function measurementFieldToWire(field: MeasurementFieldKey): string {
  return field.charAt(0).toUpperCase() + field.slice(1);
}

/** The exercise's measurement list, parsed. De-duplicated, unknowns dropped, EMPTY IN → EMPTY OUT. */
export function parseMeasurementFields(
  values: readonly (string | null | undefined)[] | null | undefined,
): MeasurementFieldKey[] {
  const seen = new Set<MeasurementFieldKey>();
  for (const raw of values ?? []) {
    const key = parseMeasurementField(raw);
    if (key) seen.add(key);
  }
  return [...seen];
}
